import React, { useState } from 'react';
import {
  Stack,
  Button,
  Chip,
  colors,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  Grid,
  DialogActions,
} from '@mui/material';
import MultiBranchAutoCompleteForFilter from '../FilterSection/MultiBranchAutoCompleteForFilter';
import MultiItemNamesAutoCompleteForFilters from '../FilterSection/MultiItemNamesAutoCompleteForFilters';
import MultiClientAutoCompleteForFilter from '../FilterSection/MultiClientAutoCompleteForFilter';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { EquipmentCategoryItem } from 'api/equipmentApi';
import { EquipmentCategoryFilterStateParams } from '../Utils/Utils';

interface EquipmentFilterSectionProps {
  filterState: EquipmentCategoryFilterStateParams;
  filterDispatch: React.Dispatch<any>;
  activeFilterCount: number;
  applyFilters: () => void;
  resetFilters: () => void;
  equipmentData: EquipmentCategoryItem[];
  isFetching: boolean;
}

const EquipmentFilterSection: React.FC<EquipmentFilterSectionProps> = ({
  filterState,
  filterDispatch,
  activeFilterCount,
  applyFilters,
  resetFilters,
  equipmentData,
  isFetching,
}) => {
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [tempFilterState, setTempFilterState] = useState(filterState);

  React.useEffect(() => {
    if (openFilterDialog) setTempFilterState(filterState);
  }, [openFilterDialog, filterState]);

  const handleOpenDialog = () => setOpenFilterDialog(true);

  const handleCancel = () => setOpenFilterDialog(false);

  const handleApply = () => {
    Object.entries(tempFilterState).forEach(([field, value]) => {
      filterDispatch({ type: 'SET_FIELD', field, value });
    });
    applyFilters();
    setOpenFilterDialog(false);
  };

  const handleClearAll = () => {
    resetFilters();
    setOpenFilterDialog(false);
  };

  return (
    <>
      <Button
        variant="text"
        startIcon={
          <FilterListRoundedIcon style={{ color: colors.grey[600] }} />
        }
        sx={{
          color: colors.grey[600],
          borderColor: colors.grey[600],
          px: '0.5rem',
        }}
        onClick={handleOpenDialog}>
        Filters
        {activeFilterCount > 0 && (
          <Chip
            label={activeFilterCount}
            sx={{
              fontWeight: 'bold',
              borderRadius: '50%',
              padding: 0,
              marginLeft: '0.5rem',
            }}
          />
        )}
      </Button>
      <Dialog
        open={openFilterDialog}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth>
        <DialogTitle>Apply Equipment Filters</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} sx={{ marginTop: '0.1rem' }}>
            <Grid item xs={12} md={6}>
              <MultiItemNamesAutoCompleteForFilters
                label="Item Names"
                onChange={(val) =>
                  setTempFilterState((prev) => ({
                    ...prev,
                    itemNames: val as EquipmentCategoryItem[],
                  }))
                }
                defaultSelected={tempFilterState.itemNames}
                data={equipmentData}
                isFetching={isFetching}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MultiBranchAutoCompleteForFilter
                label="Branches"
                onChange={(val) =>
                  setTempFilterState((prev) => ({
                    ...prev,
                    branchIds: val,
                  }))
                }
                defaultSelected={tempFilterState.branchIds}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MultiClientAutoCompleteForFilter
                label="Clients"
                onChange={(val) =>
                  setTempFilterState((prev) => ({
                    ...prev,
                    filterClient: val,
                  }))
                }
                defaultSelected={tempFilterState.filterClient}
              />
            </Grid>
          </Grid>
          <DialogActions sx={{ marginTop: '1rem' }}>
            <Stack
              spacing={1.5}
              direction="row"
              sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} sx={{ color: colors.grey[600] }}>
                Cancel
              </Button>
              {filterState.itemNames.length > 0 ||
              filterState.branchIds.length > 0 ||
              filterState.filterClient.length > 0 ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearAll}>
                  Clear All
                </Button>
              ) : null}

              <Button variant="contained" color="primary" onClick={handleApply}>
                Apply Filters
              </Button>
            </Stack>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EquipmentFilterSection;
